import { MultiplayerService, GameRoom, PlayerInfo } from '../services/MultiplayerService';
import { AuthService } from '../services/AuthService';

export class MultiplayerRoom {
  private container: HTMLElement;
  private multiplayerService: MultiplayerService;
  private authService: AuthService;
  private roomId: string;
  private isHost: boolean = false;
  private roomUnsubscribe: (() => void) | null = null;
  private onGameStart: (boardData: any) => void;
  private onLeaveRoom: () => void;

  constructor(
    container: HTMLElement,
    roomId: string,
    authService: AuthService,
    onGameStart: (boardData: any) => void,
    onLeaveRoom: () => void
  ) {
    this.container = container;
    this.roomId = roomId;
    this.authService = authService;
    this.multiplayerService = new MultiplayerService(authService);
    this.onGameStart = onGameStart;
    this.onLeaveRoom = onLeaveRoom;
    
    this.init();
  }

  private init(): void {
    this.render();
    this.listenToRoom();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="multiplayer-room">
        <div class="room-header">
          <h2>ゲームルーム</h2>
          <div class="room-id">Room ID: ${this.roomId.slice(-6)}</div>
        </div>
        
        <div class="players-grid" id="players-grid">
          <div class="loading">プレイヤー情報を読み込み中...</div>
        </div>
        
        <div class="room-controls">
          <button id="ready-btn" class="btn-ready">
            準備完了
          </button>
          <button id="start-btn" class="btn-start" style="display: none;">
            ゲーム開始
          </button>
          <button id="leave-btn" class="btn-leave">
            ルームから退出
          </button>
        </div>
        
        <div class="room-info">
          <p>💡 全員が「準備完了」を押すとゲームを開始できます</p>
          <p>💡 同じ盤面でタイムを競います</p>
        </div>
      </div>
    `;
    
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const readyBtn = document.getElementById('ready-btn');
    const startBtn = document.getElementById('start-btn');
    const leaveBtn = document.getElementById('leave-btn');
    
    readyBtn?.addEventListener('click', () => this.toggleReady());
    startBtn?.addEventListener('click', () => this.startGame());
    leaveBtn?.addEventListener('click', () => this.leaveRoom());
  }

  private listenToRoom(): void {
    this.roomUnsubscribe = this.multiplayerService.listenToRoom(
      this.roomId,
      (room) => this.handleRoomUpdate(room)
    );
  }

  private handleRoomUpdate(room: GameRoom | null): void {
    if (!room) {
      alert('ルームが削除されました');
      this.onLeaveRoom();
      return;
    }
    
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;
    
    this.isHost = room.hostId === currentUser.uid;
    
    // ゲーム開始チェック
    if (room.status === 'playing' && room.boardData) {
      this.onGameStart(room.boardData);
      return;
    }
    
    // プレイヤー一覧を更新
    this.updatePlayersList(room.players);
    
    // コントロールボタンの表示を更新
    this.updateControls(room, currentUser.uid);
  }

  private updatePlayersList(players: { [uid: string]: PlayerInfo }): void {
    const grid = document.getElementById('players-grid');
    if (!grid) return;
    
    const playersList = Object.values(players);
    
    grid.innerHTML = playersList.map((player, index) => `
      <div class="player-card ${player.status}">
        <div class="player-number">P${index + 1}</div>
        <div class="player-name">${this.escapeHtml(player.displayName)}</div>
        <div class="player-status-badge">
          ${this.getStatusBadge(player.status)}
        </div>
        ${player.status === 'playing' ? `
          <div class="player-score">スコア: ${player.score}</div>
        ` : ''}
        ${player.status === 'finished' && player.completionTime ? `
          <div class="player-time">
            ⏱ ${this.formatTime(player.completionTime)}
          </div>
        ` : ''}
      </div>
    `).join('');
  }

  private updateControls(room: GameRoom, currentUserId: string): void {
    const readyBtn = document.getElementById('ready-btn') as HTMLButtonElement;
    const startBtn = document.getElementById('start-btn') as HTMLButtonElement;
    
    const currentPlayer = room.players[currentUserId];
    if (!currentPlayer) return;
    
    // 準備ボタンの状態
    if (readyBtn) {
      if (currentPlayer.status === 'ready') {
        readyBtn.textContent = '準備をキャンセル';
        readyBtn.classList.add('ready');
      } else {
        readyBtn.textContent = '準備完了';
        readyBtn.classList.remove('ready');
      }
      
      readyBtn.style.display = this.isHost ? 'none' : 'block';
    }
    
    // 開始ボタンの表示（ホストのみ）
    if (startBtn) {
      if (this.isHost) {
        const allReady = Object.values(room.players).every(
          p => p.status === 'ready' || p.uid === room.hostId
        );
        
        startBtn.style.display = 'block';
        startBtn.disabled = !allReady;
        startBtn.textContent = allReady ? 
          'ゲーム開始' : 
          '全員の準備を待っています...';
      } else {
        startBtn.style.display = 'none';
      }
    }
  }

  private getStatusBadge(status: string): string {
    switch (status) {
      case 'waiting':
        return '<span class="badge badge-waiting">待機中</span>';
      case 'ready':
        return '<span class="badge badge-ready">準備完了</span>';
      case 'playing':
        return '<span class="badge badge-playing">プレイ中</span>';
      case 'finished':
        return '<span class="badge badge-finished">完了</span>';
      default:
        return '';
    }
  }

  private async toggleReady(): Promise<void> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;
    
    try {
      // 現在の状態を取得
      const readyBtn = document.getElementById('ready-btn');
      const isReady = readyBtn?.classList.contains('ready');
      
      await this.multiplayerService.setPlayerReady(this.roomId, !isReady);
    } catch (error) {
      console.error('Failed to update ready status:', error);
    }
  }

  private async startGame(): Promise<void> {
    if (!this.isHost) return;
    
    try {
      const startBtn = document.getElementById('start-btn') as HTMLButtonElement;
      startBtn.disabled = true;
      startBtn.textContent = 'ゲームを開始中...';
      
      await this.multiplayerService.startGame(this.roomId);
    } catch (error: any) {
      console.error('Failed to start game:', error);
      alert(error.message || 'ゲームの開始に失敗しました');
      
      // ボタンを元に戻す
      const startBtn = document.getElementById('start-btn') as HTMLButtonElement;
      startBtn.disabled = false;
      startBtn.textContent = 'ゲーム開始';
    }
  }

  private async leaveRoom(): Promise<void> {
    const confirmed = confirm('ルームから退出しますか？');
    if (!confirmed) return;
    
    try {
      await this.multiplayerService.leaveRoom(this.roomId);
      this.destroy();
      this.onLeaveRoom();
    } catch (error) {
      console.error('Failed to leave room:', error);
    }
  }

  private formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  public destroy(): void {
    if (this.roomUnsubscribe) {
      this.roomUnsubscribe();
      this.roomUnsubscribe = null;
    }
  }
}